import React, { useCallback, useMemo, useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import {
    Box,
    Card,
    CardContent,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { TableSkeleton } from "components/skeletons/TableSkeleton";
import { EmptyState } from "components/EmptyState";
import { ErrorState } from "components/ErrorState";
import { loadingAtom, achievementLevelAtom } from "atoms/Ltool/matrixAtoms";
import { DRIVE_MODE_IDS, PHENOMENON_IDS, UNIT_IDS } from "utils/constsLtool";
import { DRIVE_MODE_IDS as HEV_DRIVE_MODE_IDS, PHENOMENON_IDS as HEV_PHENOMENON_IDS } from "utils/constsHev";
import { powertrainAtom } from "atoms/powertrainAtom";
import { densityAtom } from "atoms/densityAtom";
import { gearIdAtom } from "atoms/gearAtom";
import { useAllMatrixDataSetter } from "../utils/useAllMatrixDataSetter";
import { buildConditionIds } from "../utils/utils";
import { mappingAtom } from "atoms/mappingAtoms";
import { getMappingLabel } from "utils/mappingLabel";
import { useSnackbar } from "utils/hooks/useSnackbar";
import { ACHIEVEMENT_GROUP_IDS } from "utils/achievementConfigForm";
import { assessmentCssColor, resolveAssessmentKey, type AssessmentColorKey } from "utils/assessment";
import {
    applyMatrixView,
    MODE_SORT_KEY,
    type MatrixGroupView,
    type MatrixSort,
} from "../utils/matrixView";
import { buildRiskMarginItems } from "../utils/riskMargin";
import { AssessmentLegend } from "./AssessmentLegend";

// 先頭列（Unit/Mode）を横スクロール時に固定する sticky 指定。
const stickyCol = { position: "sticky" as const, left: 0, zIndex: 2, bgcolor: "background.paper" };
const stickyHeadCol = { ...stickyCol, zIndex: 3 };

export const AllConditionMatrix: React.FC = () => {
    const powertrain = useAtomValue(powertrainAtom);
    const gearId = useAtomValue(gearIdAtom);
    const isHev = powertrain === 'hev';
    const phenomenonIds: readonly string[] = isHev ? HEV_PHENOMENON_IDS : PHENOMENON_IDS;
    const driveModeIds: readonly string[] = isHev ? HEV_DRIVE_MODE_IDS : DRIVE_MODE_IDS;

    const conditionIds = buildConditionIds(null, isHev);
    useAllMatrixDataSetter(conditionIds);

    const allMatrixData = useAtomValue(achievementLevelAtom);
    const isLoading = useAtomValue(loadingAtom);
    const mapping = useAtomValue(mappingAtom);
    const { showSuccess, showError } = useSnackbar();

    // 表示専用の状態（検索 / ソート / 密度）。既定は query 空・sort null・comfortable で、表示は既定順のまま。
    // 密度はグローバル設定（densityAtom）に接続し、ユーザー選択を localStorage で永続・他画面と共有する。
    const [query, setQuery] = useState('');
    const [sort, setSort] = useState<MatrixSort | null>(null);
    const [density, setDensity] = useAtom(densityAtom);

    const achievementLevel = allMatrixData.data.achievement_level;

    // 取得済みデータから表示用 group を組み立てる（セル値・色判定・既定順は従来と同一）。
    const groups = useMemo<MatrixGroupView[]>(() => {
        return UNIT_IDS.map((unitId) => {
            const unitLabel = getMappingLabel(mapping, 'unit_id', unitId);
            const rows = driveModeIds.map((driveModeId) => {
                const driveLabel = getMappingLabel(mapping, 'drive_mode_id', driveModeId);
                const cells = phenomenonIds.map((phenomenonId) => {
                    const conditionKey = `${phenomenonId}-${driveModeId}`;
                    // BEV / HEV とも unit>condition の 2 階層（HEV は URL の gear 分だけが返る）。
                    const entry = achievementLevel?.[unitId]?.[conditionKey];
                    const rawAssessment = entry?.assessment;
                    // 未計算（null）は色を付けない。共通 resolver は null を none（グレー）にするが、画面は
                    // 従来どおり無色にする（Excel は未計算をグレーにする。BE が "none" を返した場合は画面もグレー）。
                    const colorKey: AssessmentColorKey = rawAssessment == null
                        ? "other"
                        : resolveAssessmentKey(rawAssessment);
                    const deviationValue = entry?.deviation_max;
                    const hasValue = deviationValue !== null && deviationValue !== undefined;
                    // 表示は単位付き、コピーは単位なしの数値のみ（欠測 "-" は両者とも "-"）。
                    const numberText = hasValue ? deviationValue.toFixed(1) : "-";
                    const text = hasValue ? `${numberText} dB` : "-";
                    return { phenomenonId, text, copyText: numberText, colorKey };
                });
                const searchText = [unitLabel, driveLabel, ...cells.map((c) => c.text)]
                    .join(' ')
                    .toLowerCase();
                return { driveModeId, label: driveLabel, cells, searchText };
            });
            return { unitId, label: unitLabel, rows };
        });
    }, [achievementLevel, driveModeIds, phenomenonIds, mapping]);

    // 判定に使われたマージン（現象グループ別）。並びは設定画面と同じ ACHIEVEMENT_GROUP_IDS。
    const riskMarginItems = useMemo(
        () => buildRiskMarginItems(
            allMatrixData.data.risk_margin,
            ACHIEVEMENT_GROUP_IDS[isHev ? 'hev' : 'bev'],
            mapping,
        ),
        [allMatrixData.data.risk_margin, isHev, mapping],
    );

    // 検索 → ソートを適用（既定は恒等）。
    const viewGroups = useMemo(() => applyMatrixView(groups, query, sort), [groups, query, sort]);

    // 列ヘッダクリックで asc → desc → 解除（既定順へ復帰）の 3 状態を巡回する。
    const handleSort = useCallback((columnId: string) => {
        setSort((prev) => {
            if (!prev || prev.columnId !== columnId) return { columnId, dir: 'asc' };
            if (prev.dir === 'asc') return { columnId, dir: 'desc' };
            return null;
        });
    }, []);

    const copyValue = useCallback((text: string) => {
        navigator.clipboard?.writeText(text).then(
            () => showSuccess(`コピーしました: ${text}`),
            () => showError('コピーに失敗しました'),
        );
    }, [showSuccess, showError]);

    // HEV は gear 単位で表示する。gear 未選択時は選択を促す（gear は header で選ぶため CTA なし）。
    if (isHev && !gearId) {
        return <EmptyState message="HEV では Gear を選択してください。" imageAlt="Gear 未選択" />;
    }

    if (isLoading) {
        return <TableSkeleton rows={8} columns={phenomenonIds.length + 1} />;
    }

    // 状態判定は従来どおり。CTA は既存 route への遷移のみ（次の一手を提示）。
    if (Object.keys(allMatrixData).length === 0) {
        return (
            <EmptyState
                message="計算セットが選択されていません。"
                actions={[{ label: "計算セットを選択", to: "/l-tools/existing-project" }]}
            />
        );
    }

    if (allMatrixData.status === 'error') {
        return (
            <ErrorState
                message="指定された条件に合致するデータがありません。"
                actions={[{ label: "データ登録へ", to: "/l-tools/data-registration" }]}
            />
        );
    }

    return (
        <Box>
            {/* 表示専用ツールバー: 検索（行絞り込み）と密度切替。データ・既定順は変えない。 */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1, flexWrap: "wrap" }}>
                <TextField
                    size="small"
                    placeholder="検索（ユニット / モード / 値）"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    inputProps={{ 'aria-label': 'マトリックス検索' }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ maxWidth: 280 }}
                />
                <Box sx={{ flexGrow: 1 }} />
                {riskMarginItems.length > 0 && (
                    <Box
                        role="group"
                        aria-label="マージン"
                        sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1.5 }}
                    >
                        <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                            マージン
                        </Typography>
                        {riskMarginItems.map((item) => (
                            <Typography key={item.groupId} variant="body2" component="span" color="text.secondary">
                                {item.text}
                            </Typography>
                        ))}
                    </Box>
                )}
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={density}
                    onChange={(_, v) => { if (v) setDensity(v); }}
                    aria-label="表示密度"
                >
                    <ToggleButton value="comfortable" aria-label="標準密度">標準</ToggleButton>
                    <ToggleButton value="compact" aria-label="高密度">高密度</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <AssessmentLegend />

            <Box sx={{ overflowX: "auto", p: 1 }}>
                <Card variant="outlined">
                    <CardContent sx={{ p: 0 }}>
                        <TableContainer>
                            <Table
                                size="small"
                                stickyHeader
                                sx={density === 'compact' ? { '& .MuiTableCell-root': { py: 0.25 } } : undefined}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={stickyHeadCol}>
                                            <TableSortLabel
                                                active={sort?.columnId === MODE_SORT_KEY}
                                                direction={sort?.columnId === MODE_SORT_KEY ? sort.dir : 'asc'}
                                                onClick={() => handleSort(MODE_SORT_KEY)}
                                            >
                                                Unit / Mode
                                            </TableSortLabel>
                                        </TableCell>
                                        {phenomenonIds.map((phenomenonId) => (
                                            <TableCell key={phenomenonId} align="center">
                                                <TableSortLabel
                                                    active={sort?.columnId === phenomenonId}
                                                    direction={sort?.columnId === phenomenonId ? sort.dir : 'asc'}
                                                    onClick={() => handleSort(phenomenonId)}
                                                >
                                                    {getMappingLabel(mapping, 'phenomenon_id', phenomenonId)}
                                                </TableSortLabel>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {viewGroups.map((group) => (
                                        <React.Fragment key={group.unitId}>
                                            <TableRow>
                                                <TableCell
                                                    colSpan={phenomenonIds.length + 1}
                                                    sx={{ bgcolor: 'action.hover', fontWeight: 700 }}
                                                >
                                                    {group.label}
                                                </TableCell>
                                            </TableRow>
                                            {group.rows.map((row) => (
                                                <TableRow key={`${group.unitId}-${row.driveModeId}`}>
                                                    <TableCell sx={{ pl: 4, fontWeight: "medium", ...stickyCol }}>
                                                        {row.label}
                                                    </TableCell>
                                                    {row.cells.map((cell) => {
                                                        const fill = assessmentCssColor(cell.colorKey);
                                                        return (
                                                            <TableCell
                                                                key={cell.phenomenonId}
                                                                align="center"
                                                                onClick={() => copyValue(cell.copyText)}
                                                                onContextMenu={(e) => { e.preventDefault(); copyValue(cell.copyText); }}
                                                                title="クリックで値をコピー"
                                                                sx={{ bgcolor: fill, cursor: "pointer" }}
                                                            >
                                                                {/* 塗りは明るいパステル固定のため、暗いテーマ（text.secondary が明るい色）でも読めるよう文字は濃くする */}
                                                                <Typography
                                                                    variant="caption"
                                                                    color={fill === "transparent" ? "text.secondary" : "common.black"}
                                                                    component="div"
                                                                >
                                                                    {cell.text}
                                                                </Typography>
                                                            </TableCell>
                                                        );
                                                    })}
                                                </TableRow>
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
};
